import React, { useContext, useEffect, useState } from "react";
import {
    MaybeAssociatedClientDevice,
    PrintersRegistryInfo,
} from "@grabcad/company-server-shared/dist/types";

import { ApplicationContext } from "../../../components/ApplicationProvider";
import { getOrphanedClients, getPrintersRegistry } from "../../../zeus-client/gatewaysApis";
import { getPrinterImage } from "../../../utils/printerImages";

import DeviceFilters from "../DeviceFilters/DeviceFilters";
import Pagination from "../../Shared/Pagination/Pagination";
import PrinterGridShimmer from "./PrinterGridShimmer/PrinterGridShimmer";
import NoPrintersFound from "./NoPrintersFound/NoPrintersFound";
import PrinterCardGrid from "./PrinterCardGrid/PrinterCardGrid";

import { Container, Footer } from "./Printers.styles";

const DEFAULT_ROWS_PER_PAGE = 6;

type PrinterViewModel = PrintersRegistryInfo & {
    imageUri: string;
    clientDeviceThingName?: string;
    ipAddress?: string;
};

type PrinterRegistryWithOptionalIpAddress = PrintersRegistryInfo & {
    ipAddress?: string;
};

function findMatchedClientDevice(
    printer: PrintersRegistryInfo,
    clientDevices: MaybeAssociatedClientDevice[]
): MaybeAssociatedClientDevice | undefined {
    return clientDevices.find(
        (clientDevice) =>
            clientDevice.thingName === printer.serial ||
            clientDevice.externalPrinterId === printer.serial
    );
}

const Printers: React.FC = () => {
    const { t } = useContext(ApplicationContext);

    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    const [searchTerm, setSearchTerm] = useState("");
    const [printers, setPrinters] = useState<PrinterViewModel[]>([]);

    const normalizedSearch = searchTerm.trim().toUpperCase();

    const filteredPrinters = normalizedSearch
        ? printers.filter((printer) => {
              const serial = printer.serial?.trim().toUpperCase() ?? "";
              return serial.includes(normalizedSearch);
          })
        : printers;

    const shouldShowPagination = filteredPrinters.length > DEFAULT_ROWS_PER_PAGE;

    const totalPages = Math.max(1, Math.ceil(filteredPrinters.length / rowsPerPage));

    const visiblePrinters = filteredPrinters.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    useEffect(() => {
        const load = async (): Promise<void> => {
            try {
                setLoading(true);

                const [data, clientDevices] = await Promise.all([
                    getPrintersRegistry(),
                    getOrphanedClients().catch(() => []),
                ]);

                const printersRegistry = data as PrinterRegistryWithOptionalIpAddress[];

                setPrinters(
                    printersRegistry.map((printer) => {
                        const matchedClientDevice = findMatchedClientDevice(printer, clientDevices);

                        return {
                            ...printer,
                            imageUri: getPrinterImage(printer.printerType),
                            clientDeviceThingName: matchedClientDevice?.thingName,
                            ipAddress: printer.ipAddress ?? matchedClientDevice?.ipAddress,
                        };
                    })
                );
            } catch (error) {
                console.error("Failed to load printers registry", error);
            } finally {
                setLoading(false);
            }
        };

        void load();
    }, []);

    const handleSearch = (value: string): void => {
        setSearchTerm(value);
        setPage(1);
    };

    const handleRowsChange = (rows: number): void => {
        setRowsPerPage(rows);
        setPage(1);
    };

    return (
        <Container>
            <DeviceFilters
                value={searchTerm}
                placeholder={t("devices.printer_serial_number")}
                onChange={handleSearch}
            />

            {loading ? (
                <PrinterGridShimmer />
            ) : filteredPrinters.length === 0 ? (
                <NoPrintersFound />
            ) : (
                <>
                    <PrinterCardGrid printers={visiblePrinters} />

                    {shouldShowPagination && (
                        <Footer>
                            <Pagination
                                currentPage={page}
                                totalPages={totalPages}
                                totalItems={filteredPrinters.length}
                                rowsPerPage={rowsPerPage}
                                itemLabel={t("devices.device_tabs.printers")}
                                onPageChange={setPage}
                                onRowsChange={handleRowsChange}
                            />
                        </Footer>
                    )}
                </>
            )}
        </Container>
    );
};

export default Printers;
